import { FunctionComponent, useState, useEffect, useMemo, useCallback } from 'react'
import CustomModal from '../../CustomModal'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers';
import Row from '../../../Row'
import Text from '../../../Text'
import styled from 'styled-components'
import Switch from '../../../Switch';
import Column from '../../../Column';
import Select from '../../../select/Select';
import AsyncSelect from '../../../select/AsyncSelect';
import debounce from 'lodash.debounce';
import { useHubspotWebhook } from '../../../../services/apis/hubspot/useHubspotWebhook';

type ExportType = 'contacts' | 'list' | 'workflow' | 'deals';

interface IExportDetails {
    listId?: string;
    workflowId?: string;
    dealStage?: string;
    dealName?: string;
    amount?: number;
    pipelineId?: string;
}

interface IExportToHubspotModalProps {
    numberOfConnections?: number,
    selectedAllConnections?: boolean,
    onClickHandler: (
        includeTags: boolean, 
        includeNotes: boolean, 
        exportAll: boolean, 
        useDefaultMapping: boolean, 
        exportType: ExportType, 
        exportDetails?: IExportDetails
    ) => void
}

const ExportToHubspotModal: FunctionComponent<IExportToHubspotModalProps> = ({ onClickHandler, numberOfConnections, selectedAllConnections = false }) => {

    const { hideModal, showErrorToast } = useAppHandlers()
    const { getHubspotLists, getHubspotWorkflows, getHubspotDealPipelines } = useHubspotWebhook()
    
    const [includeTags, setIncludeTags] = useState(false)
    const [includeNotes, setIncludeNotes] = useState(false)
    const [useDefaultMapping, setUseDefaultMapping] = useState(true)
    const [exportType, setExportType] = useState<ExportType>('contacts')
    
    // State for lists
    const [hubspotLists, setHubspotLists] = useState<any[]>([])
    const [selectedList, setSelectedList] = useState<any>(null)
    const [loadingLists, setLoadingLists] = useState(false)

    // State for workflows
    const [hubspotWorkflows, setHubspotWorkflows] = useState<any[]>([])
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null)
    const [loadingWorkflows, setLoadingWorkflows] = useState(false)

    // State for deals
    const [dealPipelines, setDealPipelines] = useState<any[]>([])
    const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null)
    const [selectedStageId, setSelectedStageId] = useState<string | null>(null)
    const [dealName, setDealName] = useState<string>('{firstName} {lastName} - New Deal')
    const [dealAmount, setDealAmount] = useState<number | undefined>(undefined)
    const [loadingPipelines, setLoadingPipelines] = useState(false)

    const description = `Choose how you want to export your contacts to HubSpot.`

    // Fetch lists when export type changes to 'list'
    useEffect(() => {
        if (exportType === 'list' && hubspotLists.length === 0) {
            fetchHubspotLists()
        }
    }, [exportType])

    // Fetch workflows when export type changes to 'workflow'
    useEffect(() => {
        if (exportType === 'workflow' && hubspotWorkflows.length === 0) {
            fetchHubspotWorkflows()
        }
    }, [exportType])

    // Fetch pipelines when export type changes to 'deals'
    useEffect(() => {
        if (exportType === 'deals' && dealPipelines.length === 0) {
            fetchDealPipelines()
        }
    }, [exportType])

    const fetchHubspotLists = async () => {
        try {
            setLoadingLists(true)
            const result = await getHubspotLists(undefined) // Fetch first 100 lists without search
            if (result?.lists) {
                setHubspotLists(result.lists)
            }
        } catch (error) {
            console.error('Error fetching HubSpot lists:', error)
            showErrorToast({ message: 'Failed to load HubSpot lists' })
        } finally {
            setLoadingLists(false)
        }
    }
    
    // Load options for AsyncSelect (lists) - called when user types
    const loadListOptions = useMemo(
        () => debounce(async (inputValue: string, callback: any) => {
            try {
                // Only search if user has typed something
                if (!inputValue || !inputValue.trim()) {
                    callback([]);
                    return;
                }
                
                const result = await getHubspotLists(inputValue.trim());
                
                if (result?.lists) {
                    const options = result.lists.map((list: any) => ({
                        value: list.id,
                        label: list.name,
                        ...list
                    }));
                    callback(options);
                } else {
                    callback([]);
                }
            } catch (error) {
                console.error('Error fetching HubSpot lists:', error);
                callback([]);
            }
        }, 500),
        [getHubspotLists]
    );
    
    // Convert hubspotLists to options format for defaultOptions
    const defaultListOptions = useMemo(() => {
        return hubspotLists.map((list: any) => ({
            value: list.id,
            label: list.name,
            ...list
        }));
    }, [hubspotLists]);

    const fetchHubspotWorkflows = async () => {
        try {
            setLoadingWorkflows(true)
            const result = await getHubspotWorkflows()
            if (result?.workflows) {
                setHubspotWorkflows(result.workflows)
            }
        } catch (error) {
            console.error('Error fetching HubSpot workflows:', error)
            showErrorToast({ message: 'Failed to load HubSpot workflows' })
        } finally {
            setLoadingWorkflows(false)
        }
    }

    const fetchDealPipelines = async () => {
        try {
            setLoadingPipelines(true)
            const result = await getHubspotDealPipelines()
            if (result?.pipelines) {
                setDealPipelines(result.pipelines)
            }
        } catch (error) {
            console.error('Error fetching HubSpot deal pipelines:', error)
            showErrorToast({ message: 'Failed to load HubSpot deal pipelines' })
        } finally {
            setLoadingPipelines(false)
        }
    }
    
    const isSelectionMissing = (() => {
        if (exportType === 'list') {
            return !selectedList;
        }

        if (exportType === 'workflow') {
            return !selectedWorkflowId;
        }

        if (exportType === 'deals') {
            return !selectedPipelineId || !selectedStageId;
        }

        return false;
    })();

    const handleExport = (exportAll: boolean) => {
        // Validate based on export type
        if (exportType === 'list') {
            if (!selectedList) {
                showErrorToast({ message: 'Please select a HubSpot list' })
                return
            }
        }

        if (exportType === 'workflow') {
            if (hubspotWorkflows.length === 0) {
                showErrorToast({ message: 'No workflows available. Please create a workflow in HubSpot first.' })
                return
            }
            if (!selectedWorkflowId) {
                showErrorToast({ message: 'Please select a HubSpot workflow' })
                return
            }
        }

        if (exportType === 'deals') {
            if (dealPipelines.length === 0) {
                showErrorToast({ message: 'No deal pipelines available. Please create a pipeline in HubSpot first.' })
                return
            }
            if (!selectedPipelineId || !selectedStageId) {
                showErrorToast({ message: 'Please select both pipeline and stage' })
                return
            }
        }
        
        // Construct export details based on export type
        const exportDetails: IExportDetails = {};
        
        if (exportType === 'list') {
            exportDetails.listId = selectedList?.value || undefined;
        } else if (exportType === 'workflow') {
            exportDetails.workflowId = selectedWorkflowId || undefined;
        } else if (exportType === 'deals') {
            exportDetails.dealStage = selectedStageId || undefined;
            exportDetails.dealName = dealName;
            exportDetails.amount = dealAmount;
            exportDetails.pipelineId = selectedPipelineId || undefined;
        }
        
        onClickHandler(includeTags, includeNotes, exportAll, useDefaultMapping, exportType, exportDetails);
        hideModal();
    }

    return (
        <CustomModal
            headingText={'Export Contacts to HubSpot'}
            closeButton
            descriptionTextBold={<StyledText>{description}</StyledText>}
            confirmButton={{
                text: selectedAllConnections ? 'Export All Connections' : `Export Selected (${numberOfConnections ?? 0})`,
                extraBigButton: true,
                disable: (selectedAllConnections ? false : !numberOfConnections) || isSelectionMissing,
                onClickHandler: () => handleExport(selectedAllConnections)
            }}
            infoButton={{
                text: 'Export All',
                mediumButton: true,
                disable: isSelectionMissing,
                onClickHandler: () => handleExport(true)
            }}
            cancelButton={{
                text: 'Cancel',
                mediumButton: true,
                onClickHandler: () => hideModal()
            }}>
                <Column gap="16px">
                    <Column gap="10px">
                        <Text $bold>Export Options</Text>
                        <ExportTypeGrid>
                            <ExportTypeButton
                                selected={exportType === 'contacts'}
                                onClick={() => setExportType('contacts')}
                            >
                                <ButtonContent>
                                    <ButtonTitle>As Contacts</ButtonTitle>
                                    <ButtonDescription>Export as HubSpot contacts only</ButtonDescription>
                                </ButtonContent>
                            </ExportTypeButton>
                            
                            <ExportTypeButton
                                selected={exportType === 'list'}
                                onClick={() => setExportType('list')}
                            >
                                <ButtonContent>
                                    <ButtonTitle>Add to Segment (List)</ButtonTitle>
                                    <ButtonDescription>Export and add to a HubSpot Segment (list)</ButtonDescription>
                                </ButtonContent>
                            </ExportTypeButton>
                            
                            <ExportTypeButton
                                selected={exportType === 'workflow'}
                                onClick={() => setExportType('workflow')}
                            >
                                <ButtonContent>
                                    <ButtonTitle>Enroll in Workflow</ButtonTitle>
                                    <ButtonDescription>Export and enroll in a workflow</ButtonDescription>
                                </ButtonContent>
                            </ExportTypeButton>
                            
                            <ExportTypeButton
                                selected={exportType === 'deals'}
                                onClick={() => setExportType('deals')}
                            >
                                <ButtonContent>
                                    <ButtonTitle>Create as Deals</ButtonTitle>
                                    <ButtonDescription>Export and create deals for contacts</ButtonDescription>
                                </ButtonContent>
                            </ExportTypeButton>
                        </ExportTypeGrid>
                    </Column>

                    {/* List Selection */}
                    {exportType === 'list' && (
                        <Column gap="8px">
                            <Text $bold>Select HubSpot List</Text>
                            {loadingLists ? (
                                <Text $lighter>Loading lists...</Text>
                            ) : hubspotLists.length === 0 ? (
                                <NoListsMessage>
                                    <Text $lighter>No lists found in your HubSpot account.</Text>
                                    <Text $lighter>Please create a list in HubSpot first.</Text>
                                </NoListsMessage>
                            ) : (
                                <>
                                    <Text $lighter>Start typing to search or select from first 100 lists</Text>
                                    <SelectWrapper>
                                        <AsyncSelect
                                            value={selectedList}
                                            loadOptions={loadListOptions}
                                            defaultOptions={defaultListOptions}
                                            onChangeHandler={(option: any) => setSelectedList(option)}
                                            placeholder="Select or search lists..."
                                            isClearable
                                            maxLength={30}
                                            showMenuFooter={false}
                                        />
                                    </SelectWrapper>
                                </>
                            )}
                        </Column>
                    )}

                    {/* Workflow Selection */}
                    {exportType === 'workflow' && (
                        <Column gap="8px">
                            <Text $bold>Select HubSpot Workflow</Text>
                            {loadingWorkflows ? (
                                <Text $lighter>Loading workflows...</Text>
                            ) : hubspotWorkflows.length === 0 ? (
                                <NoListsMessage>
                                    <Text $lighter>No workflows found in your HubSpot account.</Text>
                                    <Text $lighter>Please create a workflow in HubSpot first.</Text>
                                </NoListsMessage>
                            ) : (
                                <SelectWrapper>
                                    <Select
                                        value={hubspotWorkflows.find(wf => wf.id === selectedWorkflowId) ? {
                                            value: selectedWorkflowId,
                                            label: hubspotWorkflows.find(wf => wf.id === selectedWorkflowId)?.name
                                        } : null}
                                        options={hubspotWorkflows.map(workflow => ({
                                            value: workflow.id,
                                            label: workflow.name
                                        }))}
                                        onChange={(option: any) => setSelectedWorkflowId(option?.value || null)}
                                        placeholder="Select a workflow"
                                        isClearable
                                    />
                                </SelectWrapper>
                            )}
                        </Column>
                    )}

                    {/* Deal Pipeline & Stage Selection */}
                    {exportType === 'deals' && (
                        <Column gap="12px">
                            {loadingPipelines ? (
                                <Text $lighter>Loading pipelines...</Text>
                            ) : dealPipelines.length === 0 ? (
                                <NoListsMessage>
                                    <Text $lighter>No deal pipelines found in your HubSpot account.</Text>
                                    <Text $lighter>Please create a pipeline in HubSpot first.</Text>
                                </NoListsMessage>
                            ) : (
                                <>
                                    <Column gap="8px">
                                        <Text $bold>Select Pipeline</Text>
                                        <SelectWrapper>
                                            <Select
                                                value={dealPipelines.find(p => p.id === selectedPipelineId) ? {
                                                    value: selectedPipelineId,
                                                    label: dealPipelines.find(p => p.id === selectedPipelineId)?.label
                                                } : null}
                                                options={dealPipelines.map(pipeline => ({
                                                    value: pipeline.id,
                                                    label: pipeline.label
                                                }))}
                                                onChange={(option: any) => {
                                                    setSelectedPipelineId(option?.value || null)
                                                    setSelectedStageId(null) // Reset stage when pipeline changes
                                                }}
                                                placeholder="Select a pipeline"
                                                isClearable
                                            />
                                        </SelectWrapper>
                                    </Column>

                                    {selectedPipelineId && (
                                        <Column gap="8px">
                                            <Text $bold>Select Deal Stage</Text>
                                            <SelectWrapper>
                                                <Select
                                                    value={dealPipelines.find(p => p.id === selectedPipelineId)?.stages.find((s: any) => s.id === selectedStageId) ? {
                                                        value: selectedStageId,
                                                        label: dealPipelines.find(p => p.id === selectedPipelineId)?.stages.find((s: any) => s.id === selectedStageId)?.label
                                                    } : null}
                                                    options={dealPipelines.find(p => p.id === selectedPipelineId)?.stages.map((stage: any) => ({
                                                        value: stage.id,
                                                        label: stage.label
                                                    })) || []}
                                                    onChange={(option: any) => setSelectedStageId(option?.value || null)}
                                                    placeholder="Select a stage"
                                                    isClearable
                                                />
                                            </SelectWrapper>
                                        </Column>
                                    )}
                                </>
                            )}
                        </Column>
                    )}

                    <Row gap="10px" alignItems>
                        <Switch prefilledValue={useDefaultMapping} onClickHandler={() => setUseDefaultMapping(!useDefaultMapping)} />
                        <Text>Use default field mapping</Text>
                    </Row>
                </Column>
            </CustomModal>
    )
}

const StyledText =  styled(Text)`
    display: inline-block;
    text-align: left;
    font-weight: normal;
`

const StyledBoldText = styled(Text)`
    display: inline-block;
    font-weight: bold;
`

const ExportTypeGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`

const ExportTypeButton = styled.button<{ selected: boolean }>`
    padding: 16px;
    border: 2px solid ${props => props.selected ? '#5E6AD2' : '#E0E0E0'};
    border-radius: 8px;
    background: ${props => props.selected ? '#F0F1FF' : '#FFFFFF'};
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: left;

    &:hover {
        border-color: #5E6AD2;
        background: #F0F1FF;
    }

    &:active {
        transform: scale(0.98);
    }
`

const ButtonContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const ButtonTitle = styled.div`
    font-size: 14px;
    font-weight: bold;
    color: #333;
`

const ButtonDescription = styled.div`
    font-size: 12px;
    color: #666;
    line-height: 1.4;
`

const SelectWrapper = styled.div`
    width: 100%;
`

const NoListsMessage = styled.div`
    padding: 16px;
    background: #F5F5F5;
    border-radius: 8px;
    text-align: center;
`

export default ExportToHubspotModal
export type { ExportType }
